import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import {
  BASE_CUSTOM_MAP_GEOMETRY_TYPE,
  CUSTOM_MAP_TYPE,
  POINT_MAP_ICON,
  POINT_MAP_ICON_TO_GRAPHQL,
} from "common/constants";
import {
  PointMapIcon,
  useCreateCustomMapMutation,
  useUpdateCustomMapMutation,
} from "../../../../generated/graphql";
import { useStatusToasts } from "../../../../hooks/useStatusToasts";
import { track } from "../../../../utils/tracking";
import { CommonModal } from "../../../Common/CommonModal";
import Modal from "../../../Common/Modal";
import ObjectTypePreviewIcon from "../../../Common/ObjectTypePreviewIcon";
import { ReactHookFormColorInput } from "../../../Inputs/ColorInput";
import { Text } from "../../../Inputs/react-hook-form";
import { ReactHookFormSelect as Select } from "../../../Inputs/Select";
import Switch from "../../../Inputs/Switch";
import TextInput from "../../../Inputs/Text";
import { InputGrid } from "../../Account/PropertyData/__styles__/floodInfoModals";
import {
  ColorContainer,
  FlexColumn1,
  PreviewIconBox,
  PreviewSection,
  ShapeColorRow,
  StyleRow,
  SwitchContainer,
  SwitchLabel,
  SwitchRow,
  UpsertModalContent,
} from "./__styles__/UpsertCustomMapModal";
import { CustomMapForSettings } from "./types";

interface UpsertCustomMapForm {
  name: string;
  geometryType: BASE_CUSTOM_MAP_GEOMETRY_TYPE;
  allowsCreation: boolean;
  icon: POINT_MAP_ICON;
  iconSize: number;
  fillColor: string;
  fillOpacity: number;
  lineColor: string;
  lineOpacity: number;
  lineWidth: number;
}

const DEFAULT_STYLES = {
  point: {
    icon: POINT_MAP_ICON.CIRCLE,
    iconSize: 0.5,
    fillColor: "#2868EA",
    fillOpacity: 0.5,
    lineColor: "#FFFFFF",
    lineOpacity: 1,
    lineWidth: 1,
  },
  line: {
    icon: POINT_MAP_ICON.CIRCLE,
    iconSize: 0.5,
    fillColor: "#2868EA",
    fillOpacity: 0.5,
    lineColor: "#D97E00",
    lineOpacity: 0.8,
    lineWidth: 2,
  },
  polygon: {
    icon: POINT_MAP_ICON.CIRCLE,
    iconSize: 0.5,
    fillColor: "#20845A",
    fillOpacity: 0.5,
    lineColor: "#20845A",
    lineOpacity: 1,
    lineWidth: 1,
  },
};

const GEOMETRY_TYPE_OPTIONS = [
  { value: BASE_CUSTOM_MAP_GEOMETRY_TYPE.POINT, label: "Point" },
  { value: BASE_CUSTOM_MAP_GEOMETRY_TYPE.LINE, label: "Line" },
  { value: BASE_CUSTOM_MAP_GEOMETRY_TYPE.POLYGON, label: "Polygon" },
];

const POINT_ICON_OPTIONS = [
  { value: POINT_MAP_ICON.CIRCLE, label: "Circle" },
  { value: POINT_MAP_ICON.CIRCLE_DOT, label: "Circle Dot" },
  { value: POINT_MAP_ICON.CIRCLE_ALERT, label: "Circle Alert" },
  { value: POINT_MAP_ICON.SQUARE, label: "Square" },
  { value: POINT_MAP_ICON.DIAMOND, label: "Diamond" },
  { value: POINT_MAP_ICON.STAR, label: "Star" },
  { value: POINT_MAP_ICON.SPARKLE, label: "Sparkle" },
];

const GRAPHQL_TO_POINT_MAP_ICON: Record<PointMapIcon, POINT_MAP_ICON> = {
  [PointMapIcon.CIRCLE]: POINT_MAP_ICON.CIRCLE,
  [PointMapIcon.CIRCLE_DOT]: POINT_MAP_ICON.CIRCLE_DOT,
  [PointMapIcon.CIRCLE_ALERT]: POINT_MAP_ICON.CIRCLE_ALERT,
  [PointMapIcon.SQUARE]: POINT_MAP_ICON.SQUARE,
  [PointMapIcon.DIAMOND]: POINT_MAP_ICON.DIAMOND,
  [PointMapIcon.STAR]: POINT_MAP_ICON.STAR,
  [PointMapIcon.SPARKLE]: POINT_MAP_ICON.SPARKLE,
};

const textByUpsertType = {
  create: {
    title: "Add map object",
    primaryButtonText: "Add",
  },
  edit: {
    title: "Edit map object",
    primaryButtonText: "Save",
  },
};

const getDefaultValuesFromCustomMap = (
  customMap: CustomMapForSettings,
): Partial<UpsertCustomMapForm> => {
  let defaults: Partial<UpsertCustomMapForm> = {
    name: customMap.name,
    allowsCreation: customMap.allowsCreation,
  };

  const styles = customMap.defaultStyles;
  if (!styles) return defaults;

  defaults.geometryType = styles.geometryType;

  if (styles.__typename === "PointDefaultStyles") {
    defaults = {
      ...defaults,
      icon: GRAPHQL_TO_POINT_MAP_ICON[styles.icon],
      iconSize: styles.iconSize,
      fillColor: styles.fillColor,
      fillOpacity: styles.fillOpacity,
      lineColor: styles.lineColor,
    };
  } else if (styles.__typename === "LineDefaultStyles") {
    defaults = {
      ...defaults,
      lineColor: styles.lineColor,
      lineOpacity: styles.lineOpacity,
      lineWidth: styles.lineWidth,
    };
  } else if (styles.__typename === "PolygonDefaultStyles") {
    defaults = {
      ...defaults,
      fillColor: styles.fillColor,
      fillOpacity: styles.fillOpacity,
      lineColor: styles.lineColor,
      lineOpacity: styles.lineOpacity,
      lineWidth: styles.lineWidth,
    };
  }

  return defaults;
};

const getDefaultValuesForCreate = (): Partial<UpsertCustomMapForm> => ({
  name: "",
  geometryType: BASE_CUSTOM_MAP_GEOMETRY_TYPE.POINT,
  allowsCreation: true,
  ...DEFAULT_STYLES.point,
});

const buildDefaultStylesInput = (formData: UpsertCustomMapForm) => {
  const { geometryType } = formData;

  if (geometryType === BASE_CUSTOM_MAP_GEOMETRY_TYPE.POINT) {
    return {
      icon: POINT_MAP_ICON_TO_GRAPHQL[formData.icon] as unknown as PointMapIcon,
      iconSize: formData.iconSize,
      fillColor: formData.fillColor,
      fillOpacity: formData.fillOpacity,
      lineColor: formData.lineColor,
    };
  } else if (geometryType === BASE_CUSTOM_MAP_GEOMETRY_TYPE.LINE) {
    return {
      lineColor: formData.lineColor,
      lineOpacity: formData.lineOpacity,
      lineWidth: formData.lineWidth,
    };
  } else {
    return {
      fillColor: formData.fillColor,
      fillOpacity: formData.fillOpacity,
      lineColor: formData.lineColor,
      lineOpacity: formData.lineOpacity,
      lineWidth: formData.lineWidth,
    };
  }
};

interface UpsertCustomMapModalProps {
  customMap: Maybe<CustomMapForSettings>;
  hideModal: () => void;
  onSave?: () => void;
}

export const UpsertCustomMapModal = ({
  customMap,
  hideModal,
  onSave,
}: UpsertCustomMapModalProps) => {
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const [createCustomMap, { loading: createLoading }] =
    useCreateCustomMapMutation();
  const [updateCustomMap, { loading: updateLoading }] =
    useUpdateCustomMapMutation();

  const isEdit = !!customMap;
  const text = isEdit ? textByUpsertType.edit : textByUpsertType.create;

  const defaultValues =
    isEdit ?
      {
        ...getDefaultValuesFromCustomMap(customMap),
      }
    : getDefaultValuesForCreate();

  const {
    handleSubmit,
    register,
    control,
    watch,
    reset,
    getValues,
    formState: { errors, isValid },
  } = useForm<UpsertCustomMapForm>({
    defaultValues,
    mode: "onChange",
  });

  const watchedValues = watch();
  const geometryType = watchedValues.geometryType;

  const handleGeometryTypeChange = (
    newType: Maybe<BASE_CUSTOM_MAP_GEOMETRY_TYPE>,
  ) => {
    if (!newType) return;

    reset({
      ...getValues(),
      geometryType: newType,
      ...DEFAULT_STYLES[newType],
    });
  };

  const onSubmit = async (formData: UpsertCustomMapForm) => {
    if (isEdit) {
      await updateCustomMap({
        variables: {
          data: {
            id: customMap.id,
            name: formData.name,
            allowsCreation: formData.allowsCreation,
            defaultStyles: buildDefaultStylesInput(formData),
          },
        },
        onCompleted: () => {
          track("Updated custom map object type", { name: formData.name });
          addSuccessToast("Object type updated successfully");
          onSave?.();
          hideModal();
        },
        onError: () => {
          addErrorToast(
            "There was an issue updating the object type. Please try again.",
          );
        },
      });
    } else {
      await createCustomMap({
        variables: {
          data: {
            name: formData.name,
            allowsCreation: formData.allowsCreation,
            defaultStyles: {
              geometryType: formData.geometryType,
              ...buildDefaultStylesInput(formData),
            },
            displayOrder: 0,
            enabled: true,
            hiddenFromPublic: false,
            isDefault: false,
            labelsVisibleOnMap: true,
            legendEnabled: true,
            isObject: true,
            type: CUSTOM_MAP_TYPE.VISUAL,
          },
        },
        onCompleted: () => {
          track("Created custom map object type", { name: formData.name });
          addSuccessToast("Object type created successfully");
          onSave?.();
          hideModal();
        },
        onError: () => {
          addErrorToast(
            "There was an issue creating the object type. Please try again.",
          );
        },
      });
    }
  };

  const getPreviewProps = () => {
    const { icon: iconType, ...rest } = watchedValues;
    return { iconType, ...rest };
  };

  const renderStyleFields = () => {
    if (geometryType === BASE_CUSTOM_MAP_GEOMETRY_TYPE.POINT) {
      return (
        <ShapeColorRow>
          <Select
            control={control}
            name="icon"
            label="Shape"
            options={POINT_ICON_OPTIONS}
            size="medium"
            required
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
          <ReactHookFormColorInput control={control} name="fillColor" inline />
        </ShapeColorRow>
      );
    }

    if (geometryType === BASE_CUSTOM_MAP_GEOMETRY_TYPE.LINE) {
      return (
        <StyleRow>
          <ColorContainer>
            <ReactHookFormColorInput
              control={control}
              name="lineColor"
              label="Outline"
            />
          </ColorContainer>
          <FlexColumn1 />
        </StyleRow>
      );
    }

    return (
      <>
        <StyleRow>
          <ColorContainer>
            <ReactHookFormColorInput
              control={control}
              name="fillColor"
              label="Fill color"
            />
          </ColorContainer>
          <FlexColumn1>
            <Controller
              control={control}
              name="fillOpacity"
              render={({ field }) => (
                <TextInput
                  name="fillOpacity"
                  label="Fill opacity"
                  type="number"
                  value={String(Math.round(field.value * 100))}
                  onChange={val => {
                    const adjusted = Math.min(
                      100,
                      Math.max(0, parseFloat(val) || 0),
                    );
                    field.onChange(Math.round(adjusted) / 100);
                  }}
                  size="medium"
                  placeholder="50"
                  postfix="%"
                />
              )}
            />
          </FlexColumn1>
        </StyleRow>
        <StyleRow>
          <ColorContainer>
            <ReactHookFormColorInput
              control={control}
              name="lineColor"
              label="Outline color"
            />
          </ColorContainer>
          <FlexColumn1 />
        </StyleRow>
      </>
    );
  };

  return (
    <CommonModal
      title={text.title}
      primaryButtonText={text.primaryButtonText}
      onSubmit={handleSubmit(formData => void onSubmit(formData))}
      disabled={!isValid}
      loading={createLoading || updateLoading}
      onCancel={hideModal}
      size="small"
      content={
        <UpsertModalContent>
          <PreviewSection>
            <PreviewIconBox>
              <ObjectTypePreviewIcon {...getPreviewProps()} size={24} />
            </PreviewIconBox>
          </PreviewSection>

          <InputGrid>
            {!isEdit && (
              <Select
                control={control}
                name="geometryType"
                label="Type"
                options={GEOMETRY_TYPE_OPTIONS}
                size="medium"
                required
                onChange={handleGeometryTypeChange}
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              />
            )}

            <Text
              label="Name of object"
              placeholder="Name"
              error={errors.name?.message}
              {...register("name", {
                required: "Name is required",
              })}
              required
            />

            {renderStyleFields()}

            <Controller
              control={control}
              name="allowsCreation"
              render={({ field }) => (
                <SwitchRow>
                  <SwitchContainer>
                    <Switch
                      name="allowsCreation"
                      value={field.value}
                      onChange={field.onChange}
                      size="small"
                    />
                  </SwitchContainer>
                  <SwitchLabel>Allow manual object creation</SwitchLabel>
                </SwitchRow>
              )}
            />
          </InputGrid>
        </UpsertModalContent>
      }
    />
  );
};

export const useUpsertCustomMapModal = ({
  onSave,
}: {
  onSave?: () => void;
}) => {
  const [customMap, setCustomMap] = useState<Maybe<CustomMapForSettings>>(null);

  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <UpsertCustomMapModal
          customMap={customMap}
          hideModal={hideModal}
          onSave={onSave}
        />
      </Modal>
    ),
    [customMap, onSave],
  );

  const showUpsertModal = (map?: CustomMapForSettings) => {
    setCustomMap(map ?? null);
    showModal();
  };

  return [showUpsertModal, hideModal] as const;
};
